import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy } from 'lucide-react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

const COLORS = ['#dc2626', '#ea580c', '#f59e0b', '#eab308', '#84cc16', '#22c55e'];

const ConsistencyView = ({ consistencyData, getDriverColor }) => {
  if (!consistencyData || !consistencyData.drivers) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="p-12 text-center">
          <p className="text-gray-400">No hay datos de consistencia disponibles</p>
        </CardContent>
      </Card>
    );
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(3);
    return `${mins}:${secs.padStart(6, '0')}`;
  };

  // Prepare scatter chart data
  const scatterData = [];
  consistencyData.drivers.forEach((driver, driverIndex) => {
    driver.all_lap_times.forEach((time, lapIndex) => {
      scatterData.push({
        driverName: driver.driver_name,
        driverNumber: driver.driver_number,
        driverIndex: driverIndex,
        lap: lapIndex + 1,
        time: time,
        color: getDriverColor(driverIndex)
      });
    });
  });

  // Group scatter data by driver for proper rendering
  const scatterDataByDriver = {};
  scatterData.forEach(point => {
    if (!scatterDataByDriver[point.driverNumber]) {
      scatterDataByDriver[point.driverNumber] = [];
    }
    scatterDataByDriver[point.driverNumber].push(point);
  });

  // Find most consistent driver (lowest coefficient of variation)
  const mostConsistent = consistencyData.drivers.reduce((prev, current) => 
    (prev.coefficient_variation < current.coefficient_variation) ? prev : current
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {consistencyData.drivers.map((driver, idx) => (
          <Card
            key={driver.driver_number}
            className="bg-neutral-900 border-red-900/30"
            style={{ borderColor: getDriverColor(idx) }}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    #{driver.driver_number}
                    {driver.driver_number === mostConsistent.driver_number && (
                      <Trophy className="w-5 h-5 text-green-500" title="Más consistente" />
                    )}
                  </CardTitle>
                  <CardDescription className="text-gray-300">
                    {driver.driver_name}
                  </CardDescription>
                </div>
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getDriverColor(idx) }}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-400">Desviación Std</p>
                  <p className="text-lg font-bold text-white">
                    ±{driver.std_deviation.toFixed(3)}s
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Coef. Variación</p>
                  <p className="text-lg font-bold text-red-400">
                    {driver.coefficient_variation.toFixed(2)}%
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-400">Más Rápida</p>
                  <p className="text-sm font-semibold text-green-400">
                    {formatTime(driver.fastest_lap)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Más Lenta</p>
                  <p className="text-sm font-semibold text-yellow-400">
                    {formatTime(driver.slowest_lap)}
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-gray-400">Rango</p>
                <p className="text-sm font-semibold text-gray-300">
                  {driver.lap_range.toFixed(3)}s
                </p>
              </div>
              
              <div>
                <p className="text-xs text-gray-400">Mediana</p>
                <p className="text-sm font-semibold text-gray-300">
                  {formatTime(driver.median_lap)}
                </p>
              </div>
              
              <Badge 
                variant="outline" 
                className="w-full justify-center border-gray-600 text-gray-300"
              >
                {driver.all_lap_times.length} vueltas
                {driver.excluded_slow_laps > 0 && (
                  <span className="ml-2 text-[10px] text-amber-400">
                    · {driver.excluded_slow_laps} lenta{driver.excluded_slow_laps > 1 ? 's' : ''} excluida{driver.excluded_slow_laps > 1 ? 's' : ''}
                  </span>
                )}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Scatter Plot - All Lap Times */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-white">Dispersión de Tiempos por Vuelta</CardTitle>
          <CardDescription className="text-gray-400">
            Visualización de todas las vueltas de cada piloto
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                type="number" 
                dataKey="lap" 
                name="Vuelta" 
                stroke="#9ca3af"
                label={{ value: 'Número de Vuelta', position: 'insideBottom', offset: -10, fill: '#9ca3af' }}
              />
              <YAxis 
                type="number" 
                dataKey="time" 
                name="Tiempo (s)" 
                stroke="#9ca3af"
                domain={['auto', 'auto']}
                label={{ value: 'Tiempo (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
              />
              <Tooltip 
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{ 
                  backgroundColor: '#1f2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                formatter={(value, name) => {
                  if (name === 'Tiempo (s)') return [formatTime(value), name];
                  return [value, name];
                }}
              />
              <Legend />
              {Object.entries(scatterDataByDriver).map(([driverNumber, data], idx) => (
                <Scatter
                  key={driverNumber}
                  name={`#${driverNumber} ${data[0].driverName}`}
                  data={data}
                  fill={data[0].color}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Box Plot Alternative - Showing range visually */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-white">Comparación de Rangos</CardTitle>
          <CardDescription className="text-gray-400">
            Visualización de dispersión de tiempos (min, mediana, max)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {consistencyData.drivers.map((driver, idx) => {
              const totalRange = driver.slowest_lap - driver.fastest_lap;
              const medianPosition = ((driver.median_lap - driver.fastest_lap) / totalRange) * 100;
              
              return (
                <div key={driver.driver_number} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getDriverColor(idx) }}
                      />
                      <span className="text-sm font-medium text-white">
                        #{driver.driver_number} {driver.driver_name}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">
                      ±{driver.std_deviation.toFixed(3)}s ({driver.coefficient_variation.toFixed(1)}%)
                    </span>
                  </div>
                  
                  <div className="relative h-12 bg-neutral-800 rounded-lg overflow-hidden">
                    {/* Range bar */}
                    <div 
                      className="absolute h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 opacity-30"
                      style={{
                        left: '0%',
                        width: '100%'
                      }}
                    />
                    
                    {/* Median indicator */}
                    <div 
                      className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                      style={{
                        left: `${medianPosition}%`
                      }}
                      title={`Mediana: ${formatTime(driver.median_lap)}`}
                    />
                    
                    {/* Labels */}
                    <div className="absolute inset-0 flex items-center justify-between px-3 text-xs text-white">
                      <span>{formatTime(driver.fastest_lap)}</span>
                      <span className="font-semibold">Mediana</span>
                      <span>{formatTime(driver.slowest_lap)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Consistency Ranking */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-white">Ranking de Consistencia</CardTitle>
          <CardDescription className="text-gray-400">
            Ordenado por coeficiente de variación (menor = más consistente)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...consistencyData.drivers]
              .sort((a, b) => a.coefficient_variation - b.coefficient_variation)
              .map((driver, position) => (
                <div 
                  key={driver.driver_number}
                  className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg border border-neutral-700"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-600 text-white font-bold">
                      {position + 1}
                    </div>
                    <div>
                      <p className="font-medium text-white">
                        #{driver.driver_number} {driver.driver_name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {driver.all_lap_times.length} vueltas
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-white">
                      {driver.coefficient_variation.toFixed(2)}%
                    </p>
                    <p className="text-xs text-gray-400">
                      ±{driver.std_deviation.toFixed(3)}s
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsistencyView;
